import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'

// ============================================================================
// RAW API RESPONSE TYPES
// ============================================================================

export interface TimeOffAllocation {
  toReqHrsDtlId: number
  toReqDtlId: number
  hours: number
  toTypeCd: string
  toSubTypeCd: string | null
}

export interface TimeOffDayDetail {
  toReqId: string
  partialRefId: string | null
  toReqDtlId: number
  durationFlag: string
  day: string
  startTime: string | null
  endTime: string | null
  timeOffAllocations: TimeOffAllocation[]
}

export interface RequestStatus {
  toReqStatusId: number
  toReqId: string
  status: string
  comments: string | null
  denialReason: string | null
  reqstActiveFlag: string
  createdBy: string
  createdTime: string
  updatedBy: string
  updatedTime: string
  onHoldStartDate: string | null
  onHoldEndDate: string | null
  approverNUID: string
  approvalGroupId: string
  mgrRequestedRetraction: string
  isResubmitWithChange: string
  torRank: number | null
  firstName: string | null
  middleName: string | null
  lastName: string | null
  fullName: string | null
}

export interface AuditHistoryItem {
  toffReqstStatusId?: number
  toffReqstId?: string
  toffReqstStatus?: string
  aprvrNuid?: string
  comments?: string | null
  denialReason?: string | null
  mgrRequestedRetraction?: string
  editResubmitFlag?: string
  torRank?: number | null
  createdBy?: string
  createdTime?: string
  lastUpdatedBy?: string
  lastUpdatedTime?: string
  onHoldStartDate?: string | null
  onHoldEndDate?: string | null
  firstName?: string | null
  middleName?: string | null
  lastName?: string | null
  fullName?: string | null
  [key: string]: any
}

export interface EmployeeDetail {
  nuid: string
  firstName: string
  middleName: string | null
  lastName: string
  fullName: string
  kpEmail?: string
  [key: string]: any
}

export interface TimeOffApiResponse {
  toReqId: string
  partialRefId: string | null
  empNUID: string
  startDate: string
  endDate: string
  isSingleDay: string
  manualReqRcvdDate: string | null
  createdBy: string
  createdTime: string
  updatedBy: string
  updatedTime: string
  employee: EmployeeDetail
  timeOffDetails: Record<string, TimeOffDayDetail>
  statuses: RequestStatus[]
  auditHistory: AuditHistoryItem[]
  isPartialRetractAllowed?: boolean
}

// ============================================================================
// DISPLAY MODEL TYPES (used by TimeOffRequestDetail component)
// ============================================================================

export interface RequestedDate {
  toReqDtlId?: number
  requestType: string
  date: string
  day?: string
  times?: string
  /**
   * Authoritative API duration flag for the day: `'F'` = Full Day,
   * `'P'` = Partial Day. Preferred over parsing `times` when resolving the
   * request's duration, since a Full Day can still carry a start/end window.
   */
  durationFlag?: string
  startTime?: string
  endTime?: string
  requestedHours: string | number
  hoursInOtherRequests: string | number
  timeOffAllocations?: TimeOffAllocation[]
  toSubTypeCd?: string | null
}

export interface HistoryEvent {
  statusCode?: string
  event: string
  date: string
  dateLabel?: string
  by?: string
  byLabel?: string
  comments?: string
  highlightMessage?: string
  commentsLabel?: string
  deniedTimeOffRequestId?: string
  deniedTimeOffRequestIdLabel?: string
  approvedTimeOffRequestId?: string
  approvedTimeOffRequestIdLabel?: string
  denialReason?: string
  denialReasonLabel?: string
  rank?: number | null
}

export interface TimeOffRequestDetails {
  statuses: any
  auditHistory: any
  // Core fields
  requestId: string
  todate: string
  hoursRequested: string | number
  timeOffReqstType: string
  status: string
  detail: string
  // Right panel – common request info
  employeeName?: string
  nuid?: string
  submittedOn?: string
  submittedBy?: string
  employeeComments?: string
  // Left panel – status-specific fields
  approvedOn?: string
  deniedOn?: string
  holdPlacedOn?: string
  holdPlacedBy?: string
  returnedOn?: string
  returnedBy?: string
  returnReason?: string
  withdrawnOn?: string
  withdrawnBy?: string
  savedOn?: string
  retractionApprovedOn?: string
  retractionDeniedOn?: string
  retractionSubmittedOn?: string
  authorizedApprover?: string
  approverComments?: string
  approvalReason?: string
  denialReason?: string
  requestRank?: number | null
  manualReqRcvdDate?: string | null
  isSingleDay?: string
  createdBy?: string
  createdTime?: string
  updatedBy?: string
  updatedTime?: string
  startDate?: string
  endDate?: string
  empNUID?: string
  employee?: EmployeeDetail
  timeOffDetails?: Record<string, TimeOffDayDetail>
  // Retraction-specific fields
  partialRefId?: string
  employeeRetractionComment?: string
  originalRequestId?: string
  // Overridden-specific fields
  overriddenOn?: string
  overriddenBy?: string
  // PDENIED audit fields (any status)
  partiallyDeniedOn?: string
  partiallyDeniedApprover?: string
  // RESUBMITTED top-level status fields
  resubmittedOn?: string
  resubmittedBy?: string
  // mgrRequestedRetraction flag (RTSUBMITTED)
  mgrRequestedRetraction?: string
  // PAPPROVED top-level status fields
  papprovedOn?: string
  papprovedBy?: string
  // PDENIED top-level status fields
  pdeniedOn?: string
  pdeniedBy?: string
  // Current request date rows
  requestedDates?: RequestedDate[]
  // History timeline
  history?: HistoryEvent[]
  // Backend-computed authorization flag for the Partial Retract button
  isPartialRetractAllowed?: boolean
}

/**
 * Returns true when any time-off allocation in the request has type code 'A' (Vacation Bid).
 * Treats the request as a vacation bid if ANY allocation is type 'A' (user-confirmed rule).
 */
export const isVacationBid = (data: TimeOffRequestDetails): boolean =>
  data.requestedDates?.some((d) => d.requestType === 'A') ?? false

// ============================================================================
// TRANSFORM: raw API response → display model
// ============================================================================

const formatDateTime = (
  isoString: string | null | undefined
): string | undefined => {
  if (!isoString) return undefined
  const d = new Date(isoString)
  if (Number.isNaN(d.getTime())) return isoString
  const date = d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  })
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
  const offsetMins = -d.getTimezoneOffset()
  const sign = offsetMins >= 0 ? '+' : '-'
  const absOffset = Math.abs(offsetMins)
  const tzHours = String(Math.floor(absOffset / 60)).padStart(2, '0')
  const tzMins = String(absOffset % 60).padStart(2, '0')
  const tz = `GMT${sign}${tzHours}:${tzMins}`
  return `${date} ${time} ${tz}`
}

// ============================================================================
// AUDIT HISTORY MAPPING
// ============================================================================

type ActorSource = 'submitter' | 'approver' | 'system-or-approver'

interface AuditStatusConfig {
  event: string
  dateLabel: string
  byLabel: string
  commentsLabel: string
  dateSource: 'createdTime' | 'lastUpdatedTime'
  actorSource: ActorSource
  denialReason?: string
}

const AUDIT_STATUS_CONFIG: Record<string, AuditStatusConfig> = {
  SUBMITTED: {
    event: 'Submitted',
    dateLabel: 'Submitted On',
    byLabel: 'Submitted By',
    commentsLabel: 'Employee Comments',
    dateSource: 'createdTime',
    actorSource: 'submitter',
  },
  SUBMITTED_ONHOLD: {
    event: 'On Hold',
    dateLabel: 'Hold Placed On',
    byLabel: 'Hold Placed By',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'system-or-approver',
  },
  SUBMITTED_RELEASED: {
    event: 'Hold Released',
    dateLabel: 'Hold Released On',
    byLabel: 'Released By',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'approver',
  },
  APPROVED: {
    event: 'Approved',
    dateLabel: 'Approved On',
    byLabel: 'Authorized Approver',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'approver',
  },
  DENIED: {
    event: 'Denied',
    dateLabel: 'Denied On',
    byLabel: 'Authorized Approver',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'approver',
  },
  RETURNED: {
    event: 'Returned',
    dateLabel: 'Returned On',
    byLabel: 'Returned By',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'approver',
  },
  WITHDRAWN: {
    event: 'Withdrawn',
    dateLabel: 'Withdrawn On',
    byLabel: 'Withdrawn By',
    commentsLabel: 'Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'submitter',
  },
  RTSUBMITTED: {
    event: 'Retraction Submitted',
    dateLabel: 'Retraction Submitted On',
    byLabel: 'Submitted By',
    commentsLabel: 'Employee Comments Regarding Retraction',
    dateSource: 'lastUpdatedTime',
    actorSource: 'submitter',
  },
  RETRACTED: {
    event: 'Retraction Approved',
    dateLabel: 'Retraction Approved On',
    byLabel: 'Authorized Approver',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'approver',
  },
  PARTIALLY_APPROVED: {
    event: 'Partially Approved',
    dateLabel: 'Partially Approved On',
    byLabel: 'Authorized Approver',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'approver',
  },
  PRETRACTED: {
  event: 'Partial Retraction Approved',
  dateLabel: 'Retraction Approved On',
  byLabel: 'Authorized Approver',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
},
  PRTDENIED: {
  event: 'Partial Retraction Denied',
  dateLabel: 'Partial Retraction Denied On',
  byLabel: 'Authorized Approver',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
  denialReason: ' Denial Reason',
},
RTDENIED:{
  event: 'Partial Retraction Denied',
  dateLabel: 'Retraction Denied On',
  byLabel: 'Authorized Approver',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
},
  PARTIALLY_DENIED: {
    event: 'Retraction Denied',
    dateLabel: 'Retraction Denied On',
    byLabel: 'Authorized Approver',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'approver',
  },
  PAPPROVED: {
  event: 'Partially Approved',
  dateLabel: 'Partially Approved On',
  byLabel: 'Authorized Approver',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
},
  PDENIED: {
    event: 'Partially Denied',
    dateLabel: 'Partially Denied On',
    byLabel: 'Authorized Approver',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'approver',
  },
  OVERRIDDEN: {
    event: 'Overridden',
    dateLabel: 'Overridden On',
    byLabel: 'Authorized Approver',
    commentsLabel: 'Approver Comments',
    dateSource: 'lastUpdatedTime',
    actorSource: 'approver',
  },
RESUBMITTED: {
  event: 'Resubmitted',
  dateLabel: 'Resubmitted On',
  byLabel: 'Resubmitted By',
  commentsLabel: 'Employee Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'submitter',
},
RESUBMITTED_ONHOLD: {
  event: 'Resubmitted On Hold',
  dateLabel: 'Hold Placed On',
  byLabel: 'Hold Placed By',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'system-or-approver',
},
RTSUBMITTED_ONHOLD: {
  event: 'On Hold',
  dateLabel: 'Hold Placed On',
  byLabel: 'Hold Placed By',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'system-or-approver',
},
PRTSUBMITTED_ONHOLD:{
    event: 'Resubmitted On Hold',
  dateLabel: 'Hold Placed On',
  byLabel: 'Hold Placed By',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'system-or-approver',
},
RESUBMITTED_RELEASED: {
  event: 'Hold Released',
  dateLabel: 'Hold Released On',
  byLabel: 'Released By',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
},
RESUBMITTED_RELEASE: {
  event: 'Hold Released',
  dateLabel: 'Hold Released On',
  byLabel: 'Released By',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
},
RTSUBMITTED_RELEASED: {
  event: 'Hold Released',
  dateLabel: 'Hold Released On',
  byLabel: 'Released By',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
},
PRTSUBMITTED_RELEASED:{
  event: 'Hold Released',
  dateLabel: 'Hold Released On',
  byLabel: 'Released By',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
},
PAPPROVED_RTDENIED: {
  event: 'Retraction Denied',
  dateLabel: 'Retraction Denied On',
  byLabel: 'Authorized Approver',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
},
OVERRIDDEN_RTDENIED: {
  event: 'Retraction Denied',
  dateLabel: 'Retraction Denied On',
  byLabel: 'Authorized Approver',
  commentsLabel: 'Approver Comments',
  dateSource: 'lastUpdatedTime',
  actorSource: 'approver',
},
PRTSUBMITTED: {
  event: 'Partial Retraction Submitted',
  dateLabel: 'Partial Retraction Submitted On',
  byLabel: 'Submitted By',
  commentsLabel: 'Employee Comments Regarding Retraction',
  dateSource: 'lastUpdatedTime',
  actorSource: 'submitter',
},
}

const decodeComment = (s: string | null | undefined): string | undefined => {
  if (!s) return undefined
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

// Shared "Name (NUID)" formatter for every field/row literally labeled "Authorized Approver".
const formatApproverWithNuid = (
  fullName: string | null | undefined,
  nuid: string | null | undefined
): string | undefined => {
  const trimmed = (fullName ?? '').trim()
  if (trimmed && nuid) return `${trimmed} (${nuid})`
  return trimmed || nuid || undefined
}

const isSystemBatchActorCheck = (updatedBy: string): boolean =>
  updatedBy.toLowerCase().includes('tortsystem') ||
  updatedBy.toLowerCase() === 'tort_batch'

const resolveSubmitterActor = (
  fullName: string,
  updatedBy: string,
  empNUID: string,
  manualReqRcvdDate?: string | null
): string => {
  const isEmployee = updatedBy === empNUID

  const isRequestorProxy =
    Boolean(manualReqRcvdDate) && !isEmployee

  const proxy = isRequestorProxy ? 'Requestor Proxy ' : ''

  return fullName
    ? `${proxy}${fullName} (${updatedBy})`
    : `${proxy}${updatedBy}`
}

const resolveSystemOrApproverActor = (
  isSystemBatchActor: boolean,
  fullName: string,
  updatedBy: string,
  byLabel?: string
): string | undefined => {
  if (isSystemBatchActor) return 'System'
  if (byLabel === 'Hold Placed By' && fullName && updatedBy) {
    return `${fullName} (${updatedBy})`
  }
  return fullName || updatedBy
}

const resolveAuditActor = (
  item: AuditHistoryItem,
  actorSource: ActorSource,
  manualReqRcvdDate?: string | null,
  byLabel?: string,
  empNUID?: string
): string | undefined => {
  const updatedBy = (item.lastUpdatedBy ?? '').trim()
  const isSystemBatchActor = isSystemBatchActorCheck(updatedBy)
  const fullName = (item.fullName ?? '').trim()

  if (actorSource === 'submitter') {
    return resolveSubmitterActor(
      fullName,
      updatedBy,
      empNUID ?? '',
      manualReqRcvdDate
    )
  }
  if (actorSource === 'system-or-approver') {
    return resolveSystemOrApproverActor(
      isSystemBatchActor,
      fullName,
      updatedBy,
      byLabel
    )
  }
  if (byLabel === 'Released By' && isSystemBatchActor) {
    return 'System'
  }

  return byLabel === 'Authorized Approver'
    ? formatApproverWithNuid(fullName, item.aprvrNuid)
    : fullName || item.aprvrNuid
}

// Vacation-bid rank is only meaningful while a request is on hold, not once the hold is released
const HOLD_PLACED_STATUS_CODES = new Set(['SUBMITTED_ONHOLD', 'RESUBMITTED_ONHOLD', 'PRTSUBMITTED_ONHOLD'])

// Kept local to the Requestor flow (not shared with Approver) so the two flows can evolve independently.
const DRAFT_SUBMISSION_STATUSES = new Set(['DRAFT', 'SUBMITTED'])

const mapAuditHistory = (
  auditItems: AuditHistoryItem[],
  includeAll = false,
  partialRefId?: string,
  manualReqRcvdDate?: string | null,
   empNUID?: string
): HistoryEvent[] =>
  // By default drop every DRAFT/SUBMITTED entry – each already surfaces elsewhere as
  // Saved On / Submitted On / Submitted By / Employee Comments, and a request edited
  // and re-saved as a draft multiple times can produce more than one such entry.
  // Pass includeAll=true to include all items (e.g. SUBMITTED_ONHOLD with one audit entry).
  (includeAll
    ? auditItems
    : auditItems.filter(
        (item) => !DRAFT_SUBMISSION_STATUSES.has(item.toffReqstStatus?.toUpperCase() ?? '')
      )
  ).map((h) => {
    const statusCode = h.toffReqstStatus ?? ''
    const cfg = AUDIT_STATUS_CONFIG[statusCode]
    const rawDate =
      cfg?.dateSource === 'lastUpdatedTime' ? h.lastUpdatedTime : h.createdTime
    return {
      statusCode,
      event: cfg?.event ?? statusCode,
      date: formatDateTime(rawDate) ?? '',
      dateLabel: cfg?.dateLabel ?? 'Date',
      by: cfg
  ? resolveAuditActor(
      h,
      cfg.actorSource,
      manualReqRcvdDate,
      cfg.byLabel,
      empNUID
    )
  : h.lastUpdatedBy,
      byLabel: cfg?.byLabel ?? 'By',
      comments: decodeComment(h.comments),
      commentsLabel: cfg?.commentsLabel ?? 'Comments',
            highlightMessage:
        (statusCode === 'PRTSUBMITTED' || statusCode === 'RTSUBMITTED') && h.mgrRequestedRetraction === 'Y'
          ? 'Approver asked for this request to be retracted.'
          : undefined,
      deniedTimeOffRequestId:
        statusCode === 'PAPPROVED' ? (partialRefId ?? undefined) : undefined,
      deniedTimeOffRequestIdLabel:
        statusCode === 'PAPPROVED' ? 'Denied Time Off Request Id' : undefined,
      approvedTimeOffRequestId:
        statusCode === 'PDENIED' ? (partialRefId ?? undefined) : undefined,

      approvedTimeOffRequestIdLabel:
        statusCode === 'PDENIED' ? 'Approved Time Off Request ID' : undefined,
      denialReason:
        statusCode === 'DENIED' ||
        statusCode === 'PDENIED' ||
        statusCode === 'RTDENIED' ||
        statusCode === 'PRTDENIED' ||
        statusCode === 'PAPPROVED_RTDENIED' ||
        statusCode === 'OVERRIDDEN_RTDENIED' ||
        statusCode === 'TORPAPPROVEDRETRACTIONDENIED'
          ? decodeComment(h.denialReason)
          : undefined,

      denialReasonLabel:
        statusCode === 'DENIED' ||
        statusCode === 'PDENIED' ||
        statusCode === 'RTDENIED' ||
        statusCode === 'PRTDENIED' ||
        statusCode === 'PAPPROVED_RTDENIED' ||
        statusCode === 'OVERRIDDEN_RTDENIED' ||
        statusCode === 'TORPAPPROVEDRETRACTIONDENIED'
          ? 'Denial Reason'
          : undefined,
      rank: HOLD_PLACED_STATUS_CODES.has(statusCode)
        ? (h.torRank ?? null)
        : undefined,
    }
  })

const buildTimeStr = (dayDetail: TimeOffDayDetail): string => {
  if (dayDetail.startTime && dayDetail.endTime) {
    return `${dayDetail.startTime} - ${dayDetail.endTime}`
  }
  return dayDetail.durationFlag === 'F' ? 'Full Day' : ''
}

const compactNameParts = (
  firstName?: string | null,
  middleName?: string | null,
  lastName?: string | null
): string =>
  [firstName, middleName, lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')

const resolveActorFullName = (
  actor: Pick<Partial<RequestStatus>, 'fullName' | 'firstName' | 'middleName' | 'lastName'>
): string => {
  const fullName = (actor.fullName ?? '').trim()
  const nameFromParts = compactNameParts(actor.firstName, actor.middleName, actor.lastName)
  return actor.middleName?.trim() ? (nameFromParts || fullName) : fullName
}

const resolveActorNuid = (...candidates: Array<string | null | undefined>): string =>
  candidates.find((candidate) => candidate?.trim())?.trim() ?? ''

const resolveSubmitterDisplay = (fullName: string, nuid: string): string => {
  const trimmedNuid = nuid.trim()
  if (fullName && trimmedNuid) return `${fullName} (${trimmedNuid})`
  return fullName || trimmedNuid
}

const resolveHoldPlacedBy = (
  updatedBy: string | undefined,
  approverName: string | undefined
): string | undefined => {
  const normalized = (updatedBy ?? '').toLowerCase().trim()
  if (normalized.includes('tortsystem') || normalized === 'tort_batch') {
    return 'System'
  }
  return approverName ?? undefined
}

const resolveResubmittedBy = (latestStatus: Partial<RequestStatus>): string => {
  const updatedBy = resolveActorNuid(latestStatus.updatedBy)
  const fullName = resolveActorFullName(latestStatus)
  return resolveSubmitterDisplay(fullName, updatedBy)
}

// ── Helpers extracted to keep transformApiResponse within cognitive-complexity limit ──

const buildRequestedDates = (raw: TimeOffApiResponse): RequestedDate[] =>
  Object.values(raw.timeOffDetails ?? {}).map((dayDetail: TimeOffDayDetail) => {
    const totalHours = (dayDetail.timeOffAllocations ?? []).reduce(
      (sum, a) => sum + (a.hours ?? 0),
      0
    )
    const dayName = new Date(dayDetail.day + 'T00:00:00').toLocaleDateString(
      'en-US',
      {
        weekday: 'long',
      }
    )
    return {
      toReqDtlId: dayDetail.toReqDtlId,
      requestType: dayDetail.timeOffAllocations?.[0]?.toTypeCd ?? '',
      date: dayDetail.day,
      day: dayName,
      times: buildTimeStr(dayDetail),
      // Preserve the authoritative Full/Partial flag so downstream edit/save
      // flows never have to (mis)infer it from the display `times` string.
      durationFlag: dayDetail.durationFlag,
      // Raw ISO start/end so partial-day overlap checks (e.g. DraftView's
      // otherPartialDayDates) can compare against this date's actual window
      // instead of re-parsing the formatted `times` display string.
      startTime: dayDetail.startTime ?? undefined,
      endTime: dayDetail.endTime ?? undefined,
      requestedHours: totalHours,
      hoursInOtherRequests: 0,
      timeOffAllocations: dayDetail.timeOffAllocations,
      toSubTypeCd: dayDetail.timeOffAllocations?.[0]?.toSubTypeCd ?? null,
    }
  })

const resolveSubmissionInfo = (
  normalStatus: string,
  latestStatus: Partial<RequestStatus>,
  lastAudit: AuditHistoryItem | null,
  raw: TimeOffApiResponse
): {
  submittedByDisplay: string | undefined
  employeeCommentsDisplay: string
} => {
  // DRAFT requests keep the in-progress comment on the latest status row. Surface
  // it so the edit form pre-fills (and thus retains) what the requestor typed;
  // without this branch drafts fall through to '' and comments appear lost.
  if (normalStatus === 'draft' || normalStatus === 'draft request') {
    return {
      submittedByDisplay: resolveSubmitterDisplay(
        resolveActorFullName(latestStatus),
        resolveActorNuid(latestStatus.updatedBy, raw.createdBy)
      ),
      employeeCommentsDisplay: decodeComment(latestStatus.comments) ?? '',
    }
  }
  if (normalStatus === 'submitted') {
    const updatedBy = resolveActorNuid(latestStatus.updatedBy, raw.createdBy)
    const fullName = resolveActorFullName(latestStatus)
    return {
      submittedByDisplay: resolveSubmitterDisplay(fullName, updatedBy),
      employeeCommentsDisplay:
        decodeComment(latestStatus.comments) ??
        decodeComment(lastAudit?.comments) ??
        '',
    }
  }
  if (normalStatus === 'resubmitted') {
    const updatedBy = resolveActorNuid(latestStatus.updatedBy, raw.updatedBy, raw.createdBy)
    const fullName = resolveActorFullName(latestStatus)
    return {
      submittedByDisplay: resolveSubmitterDisplay(fullName, updatedBy),
      employeeCommentsDisplay: decodeComment(latestStatus.comments) ?? '',
    }
  }
  if (lastAudit) {
    const auditUpdatedBy = resolveActorNuid(lastAudit.lastUpdatedBy, lastAudit.createdBy, raw.createdBy)
    const isEmp = auditUpdatedBy === raw.empNUID
    const auditFullName = resolveActorFullName(lastAudit)
    return {
      submittedByDisplay: resolveSubmitterDisplay(auditFullName, auditUpdatedBy),
      employeeCommentsDisplay: isEmp
        ? (decodeComment(lastAudit.comments) ?? '')
        : '',
    }
  }
  return { submittedByDisplay: raw.createdBy, employeeCommentsDisplay: '' }
}

const resolvePdeniedAuditFields = (
  auditItems: AuditHistoryItem[]
): { partiallyDeniedOn?: string; partiallyDeniedApprover?: string } => {
  if (auditItems.length < 2)
    return { partiallyDeniedOn: undefined, partiallyDeniedApprover: undefined }
  const pdeniedItem = auditItems.find((a) => a.toffReqstStatus === 'PDENIED')
  if (!pdeniedItem)
    return { partiallyDeniedOn: undefined, partiallyDeniedApprover: undefined }
  return {
    partiallyDeniedOn: formatDateTime(pdeniedItem.lastUpdatedTime),
    partiallyDeniedApprover: formatApproverWithNuid(
      pdeniedItem.fullName,
      pdeniedItem.aprvrNuid
    ),
  }
}

/**
 * Resolves requestor history events from audit items.
 * Drops draft/submission records and maps valid audit entries into HistoryEvent objects.
 *
 * @param _normalStatus - Normalized current status string.
 * @param auditItems - Raw audit history items from API response.
 * @param partialRefId - Reference ID for partially approved / denied requests.
 * @param manualReqRcvdDate - Manual request received date if proxy.
 * @param empNUID - Request owner NUID.
 * @returns Array of transformed history events.
 */
const resolveHistory = (
  _normalStatus: string,
  auditItems: AuditHistoryItem[],
  partialRefId?: string,
  manualReqRcvdDate?: string | null,
  empNUID?: string
): HistoryEvent[] => {
  if (!auditItems || auditItems.length === 0) {
    return []
  }
  return mapAuditHistory(
    auditItems,
    false,
    partialRefId,
    manualReqRcvdDate,
    empNUID
  )
}

const buildSpecialStatusFields = (
  normalStatus: string,
  latestStatus: Partial<RequestStatus>,
  statusDate: string | undefined,
  approverName: string | undefined,
  raw: TimeOffApiResponse
) => {
  const isRetraction =
    normalStatus === 'retracted' ||
    normalStatus === 'partially approved' ||
    normalStatus === 'partially denied' ||
    normalStatus === 'rtsubmitted' ||
    normalStatus === 'rtsubmitted_released' ||
    normalStatus === 'prtsubmitted'||
    normalStatus === 'prtsubmitted_released' ||
    normalStatus === 'approved_rtsubmitted' ||
    normalStatus === 'approved_prtsubmitted' ||
    normalStatus === 'papproved_rtdenied'
  return {
    savedOn:
      normalStatus === 'draft' || normalStatus === 'draft request'
        ? formatDateTime(raw.createdTime)
        : undefined,
    retractionApprovedOn: normalStatus === 'retracted' || normalStatus === 'pretracted' || normalStatus==="rtdenied_overridden" || normalStatus==="prtdenied_overridden" ? statusDate : undefined,
    retractionDeniedOn:
      normalStatus === 'prtdenied' ||
      normalStatus === 'papproved_rtdenied' ||
      normalStatus === 'overridden_rtdenied'
        ? statusDate
        : undefined,
    retractionSubmittedOn:
      normalStatus === 'rtsubmitted' ||
      normalStatus === 'rtsubmitted_released' ||
      normalStatus === 'prtsubmitted' ||
      normalStatus === 'prtsubmitted_released'
        ? formatDateTime(latestStatus.updatedTime)
        : undefined,
    overriddenOn:
      normalStatus === 'overridden'
        ? formatDateTime(latestStatus.updatedTime)
        : undefined,
    overriddenBy:
      normalStatus === 'overridden'
        ? formatApproverWithNuid(latestStatus.fullName, latestStatus.approverNUID)
        : undefined,
    employeeRetractionComment: isRetraction
      ?  decodeComment(latestStatus.comments)
      : undefined,
    resubmittedOn:
      normalStatus === 'resubmitted'
        ? statusDate
        : undefined,
    resubmittedBy:
      normalStatus === 'resubmitted'
        ? resolveResubmittedBy(latestStatus)
        : undefined,
    papprovedOn: normalStatus === 'papproved' ? statusDate : undefined,
    papprovedBy:
      normalStatus === 'papproved' ? (approverName ?? undefined) : undefined,
    pdeniedOn: normalStatus === 'pdenied' ? statusDate : undefined,
    pdeniedBy:
      normalStatus === 'pdenied'
        ? formatApproverWithNuid(latestStatus.fullName, latestStatus.approverNUID)
        : undefined,
  }
}

const buildStatusFields = (
  normalStatus: string,
  latestStatus: Partial<RequestStatus>,
  statusDate: string | undefined,
  approverName: string | undefined,
  raw: TimeOffApiResponse
) => {
  const onHold =
    normalStatus === 'on hold' ||
    normalStatus === 'submitted_onhold' ||
    normalStatus === 'resubmitted_onhold' ||
    normalStatus === 'rtsubmitted_onhold' ||
    normalStatus === 'prtsubmitted_onhold'
  const holdReleased =
    normalStatus === 'submitted_released' ||
    normalStatus === 'rtsubmitted_released' ||
    normalStatus === 'prtsubmitted_released' ||
    normalStatus === 'resubmitted_released' ||
    normalStatus === 'resubmitted_release'
  const holdStatus = onHold || holdReleased
  const holdPlacedByName = holdReleased
    ? resolveHoldPlacedBy(latestStatus.updatedBy, latestStatus.updatedBy ?? approverName)
    : resolveHoldPlacedBy(latestStatus.updatedBy, approverName)
  return {
    approvedOn:
      normalStatus === 'approved' ||
      normalStatus === 'partially approved' ||
      normalStatus === 'rtsubmitted' ||
      normalStatus === 'approved_prtsubmitted'
        ? statusDate
        : undefined,
    deniedOn:
      normalStatus === 'denied' ||
      normalStatus === 'partially denied' ||
      normalStatus === 'papproved_rtdenied' ||
      normalStatus === 'overridden_rtdenied'
        ? statusDate
        : undefined,
    holdPlacedOn: holdStatus ? statusDate : undefined,
    holdPlacedBy: holdStatus ? holdPlacedByName : undefined,
    returnedOn: normalStatus === 'returned' ? statusDate : undefined,
    returnedBy:
      normalStatus === 'returned' ? (approverName ?? undefined) : undefined,
    withdrawnOn: normalStatus === 'withdrawn' ? statusDate : undefined,
    // Name comes from the WITHDRAWN status entry itself (the actual withdrawer), not raw.employee, which is only the request's owner.
    withdrawnBy:
      normalStatus === 'withdrawn'
        ? resolveSubmitterDisplay(
            resolveActorFullName(latestStatus),
            resolveActorNuid(latestStatus.updatedBy, raw.updatedBy)
          )
        : undefined,
    ...buildSpecialStatusFields(
      normalStatus,
      latestStatus,
      statusDate,
      approverName,
      raw
    ),
  }
}

const transformApiResponse = (
  raw: TimeOffApiResponse
): TimeOffRequestDetails => {
  const latestStatus: Partial<RequestStatus> = raw.statuses?.[0] ?? {}
  const status = latestStatus.status ?? ''
  const normalStatus = status.toLowerCase().trim()
  const requestedDates = buildRequestedDates(raw)
  const totalHours = requestedDates.reduce(
    (sum, d) => sum + Number(d.requestedHours),
    0
  )
  const statusDate = formatDateTime(
    latestStatus.updatedTime ?? latestStatus.createdTime
  )
  const approverName =
    (latestStatus.fullName ?? '').trim() || latestStatus.approverNUID
  const auditItems = raw.auditHistory ?? []
  const lastAudit = auditItems.at(-1) ?? null
  const submittedOnDisplay = formatDateTime(
    lastAudit?.createdTime ?? raw.createdTime
  )
  const { submittedByDisplay, employeeCommentsDisplay } = resolveSubmissionInfo(
    normalStatus,
    latestStatus,
    lastAudit,
    raw
  )
  const todate =
    raw.endDate && raw.endDate !== raw.startDate
      ? `${raw.startDate} - ${raw.endDate}`
      : raw.startDate

  return {
    requestId: raw.toReqId,
    todate,
    hoursRequested: totalHours,
    timeOffReqstType: requestedDates[0]?.requestType ?? '',
    status,
    detail: '',
    employeeName: raw.employee?.employeeName ?? '',
    // empNUID is the request owner from the API contract; prefer it over
    // employee.nuid so proxy/self comparisons are stable across payload shapes.
    nuid: raw.empNUID ?? raw.employee?.nuid ?? '',
    submittedOn: submittedOnDisplay,
    submittedBy: submittedByDisplay,
    employeeComments: employeeCommentsDisplay,
    approverComments: decodeComment(latestStatus.comments) ?? '',
    denialReason: latestStatus.denialReason ?? undefined,
    authorizedApprover: formatApproverWithNuid(
      latestStatus.fullName,
      latestStatus.approverNUID
    ),
    requestRank: latestStatus.torRank,
    partialRefId: raw.partialRefId ?? undefined,
    originalRequestId: raw.partialRefId ?? undefined,
    mgrRequestedRetraction: latestStatus.mgrRequestedRetraction ?? undefined,
    statuses: raw.statuses,
    auditHistory: auditItems,
    ...buildStatusFields(
      normalStatus,
      latestStatus,
      statusDate,
      approverName,
      raw
    ),
    ...resolvePdeniedAuditFields(auditItems),
    requestedDates,
    history: resolveHistory(
      normalStatus,
      auditItems,
      raw.partialRefId ?? undefined,
      raw.manualReqRcvdDate,
      raw.empNUID
    ),
    manualReqRcvdDate: raw.manualReqRcvdDate,
    isSingleDay: raw.isSingleDay,
    isPartialRetractAllowed: raw.isPartialRetractAllowed,
    createdBy: raw.createdBy,
    createdTime: raw.createdTime,
    updatedBy: raw.updatedBy,
    updatedTime: raw.updatedTime,
    startDate: raw.startDate,
    endDate: raw.endDate,
    empNUID: raw.empNUID,
    employee: raw.employee,
  }
}

// ============================================================================
// STATE TYPE
// ============================================================================

export interface TimeOffDetailsState {
  data: TimeOffRequestDetails | null
  loading: boolean
  error: string | null
  /** HTTP status of the last getTimeOffDetails failure, used to special-case 403 messaging. */
  errorStatus: number | null
}

interface RetractTimeOffRequestPayload {
  requestId: string
  empNuid: string
  mgrRequestedRetraction: 'Y' | 'N'
  comments: string | null
}

interface PartialRetractTimeOffRequestPayload {
  requestId: string
  empNuid: string
  mgrRequestedRetraction: 'Y' | 'N'
  partialRetractIds: number[]
  comments: string | null
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: TimeOffDetailsState = {
  data: null,
  loading: false,
  error: null,
  errorStatus: null,
}

// ============================================================================
// ASYNC THUNKS
// ============================================================================

export const getTimeOffDetails = createAsyncThunk(
  'timeOffDetails/getTimeOffDetails',
  async (requestId: string, { rejectWithValue, signal }) => {
    try {
      const options = { signal }
      const url = endpoints.requester.getTimeOffRequestDetails(requestId)
      const response = await api.get(url, options)
      return transformApiResponse(response.data as TimeOffApiResponse)
    } catch (error: unknown) {
      // Status is carried separately so the UI can special-case 403 without reparsing the raw error.
      return rejectWithValue({
        message: extractApiErrorMessage(
          error,
          'Failed to fetch time off request details'
        ),
        status: (error as { response?: { status?: number } })?.response?.status,
      })
    }
  }
)

/**
 * Submits a retract request for a time-off TOR and refreshes the latest TOR details.
 *
 * The backend expects `mgrRequestedRetraction` as `Y` or `N` and `comments` as null when empty.
 */
export const retractTimeOffRequest = createAsyncThunk(
  'timeOffDetails/retractTimeOffRequest',
  async (
    payload: RetractTimeOffRequestPayload,
    { dispatch, rejectWithValue, signal }
  ) => {
    try {
      const options = { signal }
      await api.put(
        endpoints.requester.retractTimeOffRequest(payload.requestId),
        {
          empNuid: payload.empNuid,
          mgrRequestedRetraction: payload.mgrRequestedRetraction,
          comments: payload.comments,
        },
        options
      )

      await dispatch(getTimeOffDetails(payload.requestId)).unwrap()

      return payload.requestId
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to retract time off request')
      )
    }
  }
)

/**
 * Submits a partial retract request.
 */
export const partialRetractTimeOffRequest = createAsyncThunk(
  'timeOffDetails/partialRetractTimeOffRequest',
  async (
    payload: PartialRetractTimeOffRequestPayload,
    { rejectWithValue, signal }
  ) => {
    try {
      const options = { signal }

      await api.put(
        endpoints.requester.partialRetractTimeOffRequest(payload.requestId),
        {
          empNuid: payload.empNuid,
          mgrRequestedRetraction: payload.mgrRequestedRetraction,
          partialRetractIds: payload.partialRetractIds,
          comments: payload.comments,
        },
        options
      )

      return payload.requestId
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          'Failed to partially retract time off request'
        )
      )
    }
  }
)

// ============================================================================
// SLICE DEFINITION
// ============================================================================

const timeOffDetailsSlice = createSlice({
  name: 'timeOffDetails',
  initialState,
  reducers: {
    clearTimeOffDetails: (state) => {
      state.data = null
      state.error = null
      state.errorStatus = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTimeOffDetails.pending, (state) => {
      state.loading = true
      state.error = null
      state.errorStatus = null
    })
    builder.addCase(getTimeOffDetails.fulfilled, (state, action) => {
      state.loading = false
      state.data = action.payload
    })
    builder.addCase(getTimeOffDetails.rejected, (state, action) => {
      state.loading = false
      const payload = action.payload as { message: string; status?: number }
      state.error = payload.message
      state.errorStatus = payload.status ?? null
    })
  },
})

export const { clearTimeOffDetails } = timeOffDetailsSlice.actions
export default timeOffDetailsSlice.reducer
